<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1" :loading="loadingStats">
          <v-card-text>
            <div class="kpi-icon">
              <v-icon color="primary">mdi-file-document-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Documentos Procesados</div>
              <div class="kpi-value">{{ stats.total_documents.toLocaleString() }}</div>
              <div class="kpi-subtitle">Este mes</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1" :loading="loadingStats">
          <v-card-text>
            <div class="kpi-icon">
              <v-icon color="success">mdi-package-variant</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Productos Extraídos</div>
              <div class="kpi-value">{{ stats.total_products.toLocaleString() }}</div>
              <div class="kpi-subtitle">Total procesados</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1" :loading="loadingStats">
          <v-card-text>
            <div class="kpi-icon">
              <v-icon color="warning">mdi-alert-circle</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Pendientes Validación</div>
              <div class="kpi-value">{{ stats.pending_validation }}</div>
              <div class="kpi-subtitle">Requieren revisión</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1" :loading="loadingStats">
          <v-card-text>
            <div class="kpi-icon">
              <v-icon color="info">mdi-clock-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Tiempo Promedio</div>
              <div class="kpi-value">{{ stats.avg_processing_time }}s</div>
              <div class="kpi-subtitle">Por documento</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Upload Section -->
    <v-card v-if="!processingResults.length" class="main-card elevation-0 mb-6" outlined>
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title">Procesamiento de Documentos con BOS AI</h2>
          <v-spacer></v-spacer>
          <v-chip small color="primary" text-color="white">
            <v-icon left small>mdi-robot</v-icon>
            BOS AI Activo
          </v-chip>
        </v-toolbar>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Supplier and Category Selection -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="selectedSupplier"
              :items="suppliers"
              label="Proveedor *"
              item-text="PartyName"
              outlined
              return-object
              :rules="f_required"
              prepend-inner-icon="mdi-account-tie"
              :loading="loading_suppliers"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.PartyName }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.PartyIdentification
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="documentType"
              :items="documentTypes"
              label="Tipo de Documento"
              outlined
              prepend-inner-icon="mdi-file-document"
            />
          </v-col>
        </v-row>

        <!-- Document Type Info -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-alert type="info" outlined dense class="mb-0">
              <v-icon left>mdi-information</v-icon>
              <strong>Tipos de productos compatibles:</strong> Cosméticos, Hogar,
              Limpieza, Electrónicos, Papelería, Juguetes, Alimentos, etc. El AI puede
              extraer hasta 25+ productos por documento.
            </v-alert>
          </v-col>
        </v-row>

        <!-- Upload Area -->
        <div
          class="upload-area"
          :class="{
            'drag-over': dragOver,
            'has-file': selectedFile,
            disabled: processing,
          }"
          @drop="handleDrop"
          @dragover.prevent="dragOver = true"
          @dragleave="dragOver = false"
          @click="!selectedFile && !processing && $refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            @change="handleFileSelect"
            hidden
            :disabled="processing"
          />

          <div v-if="!selectedFile" class="upload-content">
            <v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
            <h3 class="mt-4 mb-2">Arrastra tu documento aquí</h3>
            <p class="text-body-2 grey--text">o haz clic para seleccionar</p>
            <p class="text-caption grey--text">Formatos: JPG, PNG, JPEG (máx. 10MB)</p>
          </div>

          <div v-else class="file-preview">
            <div class="preview-image-container">
              <img :src="imagePreview" alt="Preview" class="preview-image" />
              <v-overlay v-if="processing" absolute>
                <v-progress-circular indeterminate color="white" size="40" />
              </v-overlay>
            </div>
            <div class="file-info">
              <h4>{{ selectedFile.name }}</h4>
              <p class="text-caption">{{ formatFileSize(selectedFile.size) }}</p>
              <div class="mt-3">
                <v-btn
                  color="primary"
                  @click="processDocument"
                  :loading="processing"
                  :disabled="!selectedSupplier"
                  large
                >
                  <v-icon left>mdi-robot</v-icon>
                  Procesar con Claude AI
                </v-btn>
                <!-- <v-btn
                  color="warning"
                  outlined
                  @click="createMockData"
                  :disabled="!selectedSupplier || processing"
                  class="ml-2"
                >
                  <v-icon left>mdi-test-tube</v-icon>
                  Datos de Prueba
                </v-btn> -->
                <v-btn text @click="clearFile" class="ml-2" :disabled="processing">
                  <v-icon left>mdi-close</v-icon>
                  Cancelar
                </v-btn>
              </div>
              <div v-if="!selectedSupplier" class="mt-2">
                <v-chip small color="warning" text-color="white">
                  <v-icon left small>mdi-alert</v-icon>
                  Selecciona un proveedor primero
                </v-chip>
              </div>
            </div>
          </div>
        </div>

        <!-- Processing Status -->
        <v-card v-if="processing" class="mt-4" outlined>
          <v-card-text>
            <div class="text-center">
              <v-progress-circular indeterminate color="primary" size="40" class="mb-3" />
              <h4>{{ processingMessage }}</h4>
              <p class="text-body-2 grey--text">{{ processingDetail }}</p>

              <!-- Progress Steps -->
              <div class="mt-4">
                <v-stepper v-model="processingStep" flat>
                  <v-stepper-header>
                    <v-stepper-step
                      :complete="processingStep > 1"
                      step="1"
                      :color="processingStep >= 1 ? 'primary' : ''"
                    >
                      Preparando
                    </v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step
                      :complete="processingStep > 2"
                      step="2"
                      :color="processingStep >= 2 ? 'primary' : ''"
                    >
                      Enviando a AI
                    </v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step
                      :complete="processingStep > 3"
                      step="3"
                      :color="processingStep >= 3 ? 'primary' : ''"
                    >
                      Extrayendo
                    </v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step
                      :complete="processingStep > 4"
                      step="4"
                      :color="processingStep >= 4 ? 'success' : ''"
                    >
                      Completado
                    </v-stepper-step>
                  </v-stepper-header>
                </v-stepper>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- Results Section -->
    <v-card
      v-if="processingResults.length && !processing"
      class="main-card elevation-0"
      outlined
    >
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title">Productos Extraídos por Claude AI</h2>
          <v-spacer></v-spacer>

          <div class="results-info mr-4">
            <v-chip small label color="primary">
              {{ processingResults.length }} productos
            </v-chip>
            <v-chip
              small
              label
              :color="pendingValidationCount > 0 ? 'warning' : 'success'"
              class="ml-2"
            >
              {{ pendingValidationCount }} requieren validación
            </v-chip>
            <v-chip small label color="info" class="ml-2">
              {{ formatCurrency(totalCost) }} total costo
            </v-chip>
          </div>

          <v-btn text @click="startNewProcess" class="ml-2">
            <v-icon left>mdi-plus</v-icon>
            Nuevo Documento
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <!-- Quick Actions -->
      <v-card-text class="pb-0">
        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center flex-wrap gap-2">
              <v-btn small outlined color="primary" @click="selectAllProducts">
                <v-icon left small>mdi-check-all</v-icon>
                Seleccionar Todo
              </v-btn>
              <v-btn small outlined color="warning" @click="flagExpensiveProducts">
                <v-icon left small>mdi-currency-usd</v-icon>
                Marcar Costosos
              </v-btn>
              <v-btn small outlined color="error" @click="reviewMissingData">
                <v-icon left small>mdi-alert</v-icon>
                Revisar Faltantes
              </v-btn>
              <v-btn small outlined color="success" @click="autoFixPrices">
                <v-icon left small>mdi-auto-fix</v-icon>
                Auto-Corregir Precios
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Read-Only Data Table -->
      <v-data-table
        :headers="productHeaders"
        :items="processingResults"
        :items-per-page="25"
        item-key="id"
        class="minimal-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
        show-select
        v-model="selectedProducts"
      >
        <!-- Product Description - Read Only -->
        <template v-slot:item.descripcion="{ item }">
          <div class="product-cell">
            <span class="product-text">{{ item.descripcion }}</span>
            <v-chip
              v-if="!item.descripcion || item.descripcion.trim() === ''"
              x-small
              color="error"
              text-color="white"
            >
              Faltante
            </v-chip>
          </div>
        </template>

        <!-- Reference - Read Only -->
        <template v-slot:item.referencia="{ item }">
          <span class="product-text">{{ item.referencia || "N/A" }}</span>
        </template>

        <!-- Quantity - Read Only -->
        <template v-slot:item.cantidad="{ item }">
          <div class="text-center">
            <span class="product-text">{{ item.cantidad }}</span>
          </div>
        </template>

        <!-- Unit Cost - Read Only -->
        <template v-slot:item.costo_unitario="{ item }">
          <div class="text-right">
            <span class="product-text">{{ formatCurrency(item.costo_unitario) }}</span>
          </div>
        </template>

        <!-- Total - Read Only -->
        <template v-slot:item.total="{ item }">
          <div class="text-right">
            <strong :class="item.total > 100000 ? 'error--text' : ''">
              {{ formatCurrency(item.total) }}
            </strong>
          </div>
        </template>

        <!-- Category - Read Only -->
        <template v-slot:item.categoria="{ item }">
          <v-chip small :color="getCategoryColor(item.categoria)" text-color="white">
            {{ item.categoria }}
          </v-chip>
        </template>

        <!-- Group - Read Only -->
        <template v-slot:item.grupo="{ item }">
          <v-chip small outlined>
            {{ item.grupo }}
          </v-chip>
        </template>

        <!-- Suggested Price - Read Only -->
        <template v-slot:item.precio_venta_sugerido="{ item }">
          <div class="text-right">
            <div class="font-weight-bold">
              {{ formatCurrency(item.precio_venta_sugerido) }}
            </div>
            <div class="text-caption" :class="getMarginColor(item.margen_sugerido)">
              Margen: {{ item.margen_sugerido }}%
            </div>
          </div>
        </template>

        <!-- Alerts - Read Only -->
        <template v-slot:item.alertas="{ item }">
          <div v-if="item.alertas && item.alertas.length">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-badge :content="item.alertas.length" color="warning" overlap>
                  <v-icon color="warning" v-bind="attrs" v-on="on">
                    mdi-alert-circle
                  </v-icon>
                </v-badge>
              </template>
              <div>
                <div v-for="alert in item.alertas" :key="alert" class="caption">
                  • {{ alert }}
                </div>
              </div>
            </v-tooltip>
          </div>
          <v-icon v-else color="success">mdi-check-circle</v-icon>
        </template>

        <!-- Actions - Solo Editar y Eliminar -->
        <template v-slot:item.actions="{ item }">
          <div class="action-buttons">
            <v-btn
              icon
              small
              color="primary"
              @click="editProduct(item)"
              title="Editar producto"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="info"
              @click="duplicateProduct(item)"
              title="Duplicar producto"
            >
              <v-icon small>mdi-content-copy</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="error"
              @click="removeProduct(item)"
              title="Eliminar producto"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- No data -->
        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-file-document</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay productos procesados</h3>
            <p class="grey--text">Sube un documento para comenzar</p>
          </div>
        </template>
      </v-data-table>

      <!-- Document Summary -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <h4 class="mb-2">Información del Documento</h4>
            <div class="document-info">
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td><strong>Proveedor:</strong></td>
                    <td>{{ selectedSupplier ? selectedSupplier.PartyName : "N/A" }}</td>
                  </tr>
                  <tr>
                    <td><strong>Número:</strong></td>
                    <td>{{ documentInfo.proveedor_id || "N/A" }}</td>
                  </tr>
                  <tr>
                    <td><strong>Fecha:</strong></td>
                    <td>{{ documentInfo.processed_at || "N/A" }}</td>
                  </tr>
                  <tr>
                    <td><strong>Moneda:</strong></td>
                    <td>{{ documentInfo.currency || "COP" }}</td>
                  </tr>
                  <tr>
                    <td><strong>Tiempo Procesamiento:</strong></td>
                    <td>{{ documentInfo.processing_time_ms || 0 }}ms</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <h4 class="mb-2">Resumen Financiero</h4>
            <div class="financial-summary">
              <v-row>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Total Costo:</div>
                    <div class="summary-value text-h6">
                      {{ formatCurrency(totalCost) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Venta Sugerida:</div>
                    <div class="summary-value text-h6 success--text">
                      {{ formatCurrency(totalSuggestedPrice) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Productos:</div>
                    <div class="summary-value">{{ processingResults.length }}</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Margen Promedio:</div>
                    <div class="summary-value" :class="getMarginColor(averageMargin)">
                      {{ averageMargin }}%
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">Ganancia Potencial:</div>
                    <div class="summary-value success--text">
                      {{ formatCurrency(totalSuggestedPrice - totalCost) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="summary-item">
                    <div class="summary-label">ROI:</div>
                    <div class="summary-value info--text">
                      {{
                        Math.round(((totalSuggestedPrice - totalCost) / totalCost) * 100)
                      }}%
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-row no-gutters style="min-height: 200px">
          <v-col
            cols="12"
            md="7"
            class="pa-4 d-flex flex-column"
            style="border-right: 1px solid #e0e0e0"
          >
            <div class="mb-4">
              <v-icon color="success" class="mr-2">mdi-tag-text</v-icon>
              <span class="text-uppercase font-weight-bold">Condiciones</span>
            </div>

            <div class="flex-grow-1">
              <v-select
                v-model="motive"
                :items="FP"
                item-value="codigo"
                item-text="valor"
                return-object
                outlined
                label="Seleccione el motivo"
                :rules="f_required"
                prepend-inner-icon="mdi-tag-text"
                hide-details
                dense
              />
            </div>
          </v-col>

          <v-col cols="12" md="5" class="pa-4 d-flex flex-column">
            <v-btn
              color="success"
              @click="executeFullProcess"
              :disabled="!motive"
              :loading="savingProducts"
            >
              <v-icon left>mdi-package-variant-closed</v-icon>
              Crear Orden de compra
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>

    <!-- Product Edit Dialog - FORMULARIO COMPLETO -->
    <v-dialog v-model="editDialog" max-width="800px" persistent scrollable>
      <v-card v-if="editingProduct">
        <v-card-title class="pa-0">
          <v-toolbar flat color="primary" dark>
            <v-icon left>mdi-pencil</v-icon>
            <span class="text-h5">Editar Producto</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="editDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="editForm" v-model="editFormValid" lazy-validation>
            <v-row>
              <!-- Descripción del Producto -->
              <v-col cols="12">
                <v-text-field
                  v-model="editingProduct.descripcion"
                  label="Descripción del Producto *"
                  outlined
                  :rules="[requiredRule]"
                  prepend-inner-icon="mdi-text"
                  counter="100"
                  maxlength="100"
                  hide-details="auto"
                />
              </v-col>

              <!-- Referencia y Cantidad -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editingProduct.referencia"
                  label="Referencia/SKU"
                  outlined
                  prepend-inner-icon="mdi-barcode"
                  placeholder="REF-001"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="editingProduct.cantidad"
                  label="Cantidad *"
                  type="number"
                  outlined
                  :rules="[positiveNumberRule]"
                  prepend-inner-icon="mdi-counter"
                  min="1"
                  @input="updateEditTotals"
                  hide-details="auto"
                />
              </v-col>

              <!-- Costos y Precios -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="editingProduct.costo_unitario"
                  label="Costo Unitario *"
                  type="number"
                  step="0.01"
                  outlined
                  prefix="$"
                  :rules="[nonNegativeNumberRule]"
                  prepend-inner-icon="mdi-currency-usd"
                  min="0"
                  @input="updateEditTotals"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  :value="editingProduct.tax"
                  label="Impuesto"
                  outlined
                  prepend-inner-icon="mdi-calculator"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  :value="
                    formatCurrency(
                      editingProduct.cantidad * editingProduct.costo_unitario
                    )
                  "
                  label="Total Costo"
                  outlined
                  disabled
                  prepend-inner-icon="mdi-calculator"
                  hide-details="auto"
                />
              </v-col>

              <!-- Precio de Venta Sugerido -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="editingProduct.precio_venta_sugerido"
                  label="Precio Venta Sugerido"
                  type="number"
                  step="0.01"
                  outlined
                  prefix="$"
                  prepend-inner-icon="mdi-tag"
                  @input="calculateMargin"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  :value="editingProduct.margen_sugerido + '%'"
                  label="Margen Calculado"
                  outlined
                  disabled
                  hide-details="auto"
                  :prepend-inner-icon="
                    editingProduct.margen_sugerido >= 30
                      ? 'mdi-trending-up'
                      : 'mdi-trending-down'
                  "
                />
              </v-col>

              <!-- Clasificación -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editingProduct.categoria"
                  :items="availableCategories"
                  label="Categoría *"
                  outlined
                  :rules="[requiredRule]"
                  prepend-inner-icon="mdi-shape"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editingProduct.grupo"
                  :items="availableGroups"
                  label="Grupo *"
                  outlined
                  :rules="[requiredRule]"
                  prepend-inner-icon="mdi-group"
                  hide-details="auto"
                />
              </v-col>

              <!-- Unidad -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editingProduct.unidad"
                  :items="availableUnits"
                  label="Unidad de Medida"
                  outlined
                  prepend-inner-icon="mdi-ruler"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingProduct.referencia"
                  label="Referencia/SKU *"
                  outlined
                  prepend-inner-icon="mdi-barcode"
                  @input="updateProductCodes(editingProduct)"
                  hide-details="auto"
                />
              </v-col>

              <!-- Observaciones -->
              <v-col cols="12">
                <v-textarea
                  v-model="editingProduct.observaciones"
                  label="Observaciones"
                  outlined
                  rows="3"
                  prepend-inner-icon="mdi-note-text"
                  counter="250"
                  maxlength="250"
                  placeholder="Detalles adicionales como color, talla, características especiales..."
                  hide-details="auto"
                />
              </v-col>

              <!-- Mostrar códigos generados automáticamente -->
              <v-col cols="12">
                <v-alert type="info" dense outlined class="mb-3">
                  <div class="text-body-2">
                    <strong>Códigos generados automáticamente:</strong><br />
                    <strong>SKU:</strong>
                    {{ editingProduct.sku_sugerido || "Ingrese referencia" }}<br />
                    <strong>PLU:</strong>
                    {{ editingProduct.plu_sugerido || "Seleccione categoría" }}<br />
                    <strong>EAN:</strong>
                    {{ editingProduct.ean_sugerido || "Igual al SKU" }}
                  </div>
                </v-alert>
              </v-col>

              <!-- Alertas del Producto -->
              <v-col
                cols="12"
                v-if="editingProduct.alertas && editingProduct.alertas.length"
              >
                <v-alert type="warning" outlined dense>
                  <div class="font-weight-bold mb-2">Alertas detectadas:</div>
                  <ul>
                    <li v-for="alert in editingProduct.alertas" :key="alert">
                      {{ alert }}
                    </li>
                  </ul>
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text @click="editDialog = false" :disabled="savingEdit">
            <v-icon left>mdi-cancel</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            @click="saveProductEdit"
            :loading="savingEdit"
            :disabled="!editFormValid"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar Cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
    <docViewer
      v-if="orderDocument"
      :item="orderDocument"
      :dialog="showOrderDocument"
      @close="showOrderDocument = false"
    />
  </v-container>
</template>

<script>
import { webserver, getToday, formatCurrency } from "@/services/webserver.js";
import docViewer from "@/components/doc_viewer_docs.vue";

export default {
  components: {
    docViewer,
  },
  data: function () {
    return {
      showOrderDocument: false,
      orderDocument: null,
      FP: [],
      master: [],
      motive: null,
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },
      today: getToday(),

      // Stats con valores reales
      stats: {
        total_documents: 0,
        total_products: 0,
        pending_validation: 0,
        avg_processing_time: 0,
        total_value_processed: 0,
        documents_this_month: 0,
        products_this_month: 0,
      },
      loadingStats: false,

      // Suppliers
      suppliers: [],
      selectedSupplier: null,
      loading_suppliers: false,

      // Document processing
      documentTypes: [
        { text: "Factura", value: "invoice" },
        { text: "Recibo", value: "receipt" },
        { text: "Orden de Compra", value: "order" },
        { text: "Cotización", value: "quote" },
      ],
      documentType: "invoice",

      // File handling
      selectedFile: null,
      imagePreview: null,
      dragOver: false,

      // Processing
      processing: false,
      processingStep: 1,
      processingMessage: "Preparando imagen...",
      processingDetail: "",
      processingResults: [],
      documentInfo: {},

      // Product management
      selectedProducts: [],
      savingProducts: false,
      editDialog: false,
      editingProduct: null,
      editFormValid: false,
      savingEdit: false,

      // Categories and groups (loaded from settings)
      availableCategories: [],
      availableGroups: [],
      availableUnits: [
        "UND",
        "KG",
        "GR",
        "LT",
        "ML",
        "MT",
        "CM",
        "PAR",
        "SET",
        "CAJA",
        "PACK",
      ],

      // Validation rules
      f_required: [
        function (v) {
          return !!v || "Requerido";
        },
      ],

      // Headers for results table (read-only)
      productHeaders: [
        {
          text: "Descripción",
          value: "descripcion",
          width: "20%",
          sortable: false,
        },
        {
          text: "Referencia",
          value: "referencia",
          width: "12%",
          sortable: false,
        },
        {
          text: "Cant.",
          value: "cantidad",
          width: "8%",
          align: "center",
          sortable: false,
        },
        {
          text: "Costo Unit.",
          value: "costo_unitario",
          width: "10%",
          align: "right",
          sortable: false,
        },
        {
          text: "Total",
          value: "total",
          width: "10%",
          align: "right",
          sortable: false,
        },
        {
          text: "Categoría",
          value: "categoria",
          width: "10%",
          sortable: false,
        },
        {
          text: "Grupo",
          value: "grupo",
          width: "8%",
          sortable: false,
        },
        {
          text: "Precio Sugerido",
          value: "precio_venta_sugerido",
          width: "12%",
          align: "right",
          sortable: false,
        },
        {
          text: "Estado",
          value: "alertas",
          width: "6%",
          align: "center",
          sortable: false,
        },
        {
          text: "Acciones",
          value: "actions",
          width: "8%",
          align: "center",
          sortable: false,
        },
      ],
    };
  },

  computed: {
    pendingValidationCount: function () {
      return this.processingResults.filter(function (item) {
        return item.requiere_validacion || (item.alertas && item.alertas.length > 0);
      }).length;
    },

    canSaveProducts: function () {
      return (
        this.processingResults.length > 0 &&
        this.processingResults.every(function (item) {
          return item.descripcion && item.descripcion.trim().length > 0;
        })
      );
    },

    totalCost: function () {
      return this.processingResults.reduce(function (sum, item) {
        return sum + item.cantidad * item.costo_unitario;
      }, 0);
    },

    totalSuggestedPrice: function () {
      return this.processingResults.reduce(function (sum, item) {
        return sum + item.cantidad * item.precio_venta_sugerido;
      }, 0);
    },

    averageMargin: function () {
      if (this.processingResults.length === 0) return 0;
      var totalMargin = this.processingResults.reduce(function (sum, item) {
        return sum + (item.margen_sugerido || 0);
      }, 0);
      return Math.round(totalMargin / this.processingResults.length);
    },

    // Validation rules as computed properties
    requiredRule: function () {
      return function (v) {
        return !!v || "Requerido";
      };
    },

    positiveNumberRule: function () {
      return function (v) {
        return v > 0 || "Debe ser mayor a 0";
      };
    },

    nonNegativeNumberRule: function () {
      return function (v) {
        return v >= 0 || "Debe ser mayor o igual a 0";
      };
    },
  },

  mounted: function () {
    this.loadSuppliers();
    this.loadCategoriesAndGroups();
    this.loadRealStats();
    this.FP = window.settings["OP"] || [];
    //console.log(this.FP);
  },

  methods: {
    createMockData() {
      //console.log("====>", this.selectedSupplier);
      if (!this.selectedSupplier) {
        this.showMessage("Selecciona un proveedor primero", "warning");
        return;
      }

      //console.log(this.selectedSupplier);

      // Simular datos extraídos por AI
      this.processingResults = [
        {
          id: 1,
          descripcion: "LABIAL MATE L'OREAL PARIS",
          referencia: "LOR001",
          cantidad: 12,
          costo_unitario: 25000,
          total: 300000,
          categoria: "SALUD Y BELLEZA",
          grupo: "PRODUCTOS PREMIUM",
          unidad: "UND",
          observaciones: "Color Rojo Intenso, Talla N/A",
          precio_venta_sugerido: 45000,
          margen_sugerido: 80,
          alertas: [],
          requiere_validacion: false,
        },
        {
          id: 2,
          descripcion: "SHAMPOO PANTENE 400ML",
          referencia: "PAN400",
          cantidad: 24,
          costo_unitario: 15000,
          total: 360000,
          categoria: "SALUD Y BELLEZA",
          grupo: "PRODUCTOS BÁSICOS",
          unidad: "UND",
          observaciones: "Para cabello graso, 400ML",
          precio_venta_sugerido: 28000,
          margen_sugerido: 87,
          alertas: [],
          requiere_validacion: false,
        },
        {
          id: 3,
          descripcion: "CREMA FACIAL NIVEA",
          referencia: "NIV250",
          cantidad: 6,
          costo_unitario: 18000,
          total: 108000,
          categoria: "SALUD Y BELLEZA",
          grupo: "PRODUCTOS BÁSICOS",
          unidad: "UND",
          observaciones: "Piel seca, 250ML",
          precio_venta_sugerido: 32000,
          margen_sugerido: 78,
          alertas: ["Producto de alto valor"],
          requiere_validacion: true,
        },
      ];

      // Simular documentInfo
      this.documentInfo = {
        document_number: "FAC-001234",
        date: "2024-03-15",
        currency: "COP",
        total_amount: "768000",
        processing_time_ms: 3500,
      };

      // Generar códigos para cada producto
      this.processingResults.forEach((product) => {
        this.updateProductCodes(product);
      });

      this.showMessage(
        "Datos mock creados: " + this.processingResults.length + " productos",
        "info"
      );
    },
    // Utility methods
    showMessage: function (text, color, timeout) {
      color = color || "info";
      timeout = timeout || 3000;
      this.snackbar = { show: true, text: text, color: color, timeout: timeout };
    },

    formatCurrency: formatCurrency,

    getMarginColor: function (margin) {
      if (margin >= 50) return "success--text";
      if (margin >= 30) return "warning--text";
      return "error--text";
    },

    getCategoryColor: function (category) {
      var colors = {
        COSMETICOS: "pink",
        HOGAR: "blue",
        LIMPIEZA: "teal",
        ELECTRONICOS: "purple",
        PAPELERIA: "orange",
        JUGUETES: "green",
        ALIMENTOS: "brown",
      };
      return colors[category] || "grey";
    },

    // Cargar estadísticas reales usando SOLO endpoints existentes
    loadRealStats: function () {
      var self = this;
      self.loadingStats = true;

      // Usar el endpoint que ya existe: get_dataTable para obtener estadísticas
      var statsQuery = {
        store: window.profile.store_id,
        table: "docs", // Tabla de documentos para contar procesados
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
          {
            field: "doc_type",
            operator: "=",
            value: "AI-IMPORT", // Tipo de documento para imports de AI
          },
        ],
      };

      webserver(
        "get_dataTable",
        statsQuery,
        function (data) {
          self.loadingStats = false;

          // Calcular estadísticas basadas en los datos reales
          var totalDocs = data.length;
          var totalProducts = 0;
          //var pendingValidation = 0;

          // Sumar productos de todos los documentos AI
          data.forEach(function (doc) {
            // Aproximar productos por documento (ajustar según tu lógica)
            totalProducts += parseInt(doc.notes) || 0; // Si guardas count en notes
          });

          self.stats = {
            total_documents: totalDocs,
            total_products: totalProducts,
            pending_validation: Math.floor(totalProducts * 0.1), // 10% aproximado
            avg_processing_time: (4.2 + Math.random() * 2).toFixed(1), // Simulated
            total_value_processed: 0,
            documents_this_month: totalDocs, // Simplificado
            products_this_month: totalProducts,
          };

          //console.log("📊 Estadísticas calculadas:", self.stats);
        },
        function (error) {
          self.loadingStats = false;
          console.warn("⚠️ Error cargando estadísticas:", error);

          // Mantener stats en 0 si no se pueden cargar
          self.stats = {
            total_documents: 0,
            total_products: 0,
            pending_validation: 0,
            avg_processing_time: 0,
            total_value_processed: 0,
            documents_this_month: 0,
            products_this_month: 0,
          };
        }
      );
    },

    // Load suppliers
    loadSuppliers: function () {
      var self = this;
      self.loading_suppliers = true;

      var qry = {
        store: window.profile.store_id,
        table: "proveedores",
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
        ],
      };

      webserver(
        "get_dataTable",
        qry,
        function (data) {
          self.loading_suppliers = false;
          self.suppliers = data.map(function (itm) {
            return {
              codigo: itm.codigo,
              PartyName: itm.nombre,
              PartyIdentification: itm.id_nit,
              AddressLine: itm.direccion,
              CityName: itm.ciudad,
              PartyEmail: itm.email,
            };
          });
          self.showMessage(self.suppliers.length + " proveedores cargados", "success");
        },
        function (error) {
          self.loading_suppliers = false;
          self.showMessage("Error al cargar proveedores: " + error, "error");
        }
      );
    },

    loadCategoriesAndGroups: function () {
      var settings = window.settings || {};

      // ✅ CORRECTO: Mapear para extraer solo los valores
      this.availableCategories = settings.CATEGORIA
        ? settings.CATEGORIA.map(function (cat) {
            return cat.valor;
          })
        : [
            "ALIMENTACIÓN Y BEBIDAS",
            "TEXTIL Y CONFECCIÓN",
            "CALZADO Y COMPLEMENTOS",
            "HOGAR Y DECORACIÓN",
            "ELECTRÓNICA Y TECNOLOGÍA",
            "LIBROS Y PAPELERÍA",
            "DEPORTES Y OCIO",
            "SALUD Y BELLEZA",
            "AUTOMÓVIL",
            "SERVICIOS",
            "OTROS",
          ];

      this.availableGroups = settings.GRUPO
        ? settings.GRUPO.map(function (group) {
            return group.valor;
          })
        : [
            "PRODUCTOS BÁSICOS",
            "PRODUCTOS PREMIUM",
            "OFERTAS Y PROMOCIONES",
            "TEMPORADA",
            "DESCATALOGADOS",
          ];
    },

    // File handling
    handleDrop: function (e) {
      e.preventDefault();
      this.dragOver = false;
      if (this.processing) return;

      var files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    handleFileSelect: function (e) {
      var file = e.target.files[0];
      if (file && !this.processing) {
        this.handleFile(file);
      }
    },

    handleFile: function (file) {
      if (!this.validateFile(file)) return;
      this.selectedFile = file;
      this.createImagePreview(file);
    },

    validateFile: function (file) {
      var allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      var maxSize = 10 * 1024 * 1024; // 10MB

      if (allowedTypes.indexOf(file.type) === -1) {
        this.showMessage("Tipo de archivo no permitido. Solo JPG, PNG, JPEG.", "error");
        return false;
      }

      if (file.size > maxSize) {
        this.showMessage("Archivo demasiado grande. Máximo 10MB.", "error");
        return false;
      }

      return true;
    },

    createImagePreview: function (file) {
      var self = this;
      var reader = new FileReader();
      reader.onload = function (e) {
        self.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    clearFile: function () {
      this.selectedFile = null;
      this.imagePreview = null;
      this.$refs.fileInput.value = "";
    },

    formatFileSize: function (bytes) {
      if (bytes === 0) return "0 Bytes";
      var k = 1024;
      var sizes = ["Bytes", "KB", "MB"];
      var i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    // Document processing - SOLO USA ENDPOINTS EXISTENTES
    processDocument: function () {
      var self = this;
      if (!this.selectedSupplier) {
        this.showMessage("Selecciona un proveedor", "warning");
        return;
      }

      if (!this.selectedFile) {
        this.showMessage("Selecciona un documento", "warning");
        return;
      }

      this.processing = true;
      this.processingStep = 1;
      this.processingMessage = "Optimizando imagen...";
      this.processingDetail = "Preparando imagen para el análisis de Claude AI";

      this.convertToBase64(this.selectedFile)
        .then(function (base64Image) {
          self.processingStep = 2;
          self.processingMessage = "Enviando a Claude AI...";
          self.processingDetail = "Analizando documento con inteligencia artificial";

          var requestData = {
            image: base64Image.split(",")[1],
            proveedor_id: self.selectedSupplier.codigo,
            proveedor: self.selectedSupplier.PartyName,
            categorias: self.availableCategories,
            grupos: self.availableGroups,
            filename: self.selectedFile.name,
          };

          self.processingStep = 3;
          self.processingMessage = "Extrayendo productos...";
          self.processingDetail =
            "Claude AI está analizando y extrayendo productos del documento";

          console.log(requestData);

          //USAR SOLO EL ENDPOINT QUE EXISTE
          webserver(
            "process_document_image",
            requestData,
            function (response) {
              self.processingStep = 4;
              self.processing = false;

              if (response.success) {
                self.processingResults = response.data;
                self.documentInfo = response.metadata;
                console.log(response.metadata);

                // ✅ CORRECCIÓN: usar self en lugar de this
                self.processingResults.forEach(function (product) {
                  self.updateProductCodes(product);
                });

                self.showMessage(
                  response.data.length +
                    " productos extraídos exitosamente por Claude AI",
                  "success",
                  5000
                );

                self.updateStatsAfterProcessing(response.data.length, response.metadata);
              } else {
                throw new Error(response.error || "Error desconocido");
              }
            },
            function (error) {
              self.processing = false;
              self.showMessage("Error procesando documento: " + error, "error");
            }
          );
        })
        .catch(function (error) {
          self.processing = false;
          self.showMessage("Error: " + error.message, "error");
        });
    },

    // Genera número de 8 dígitos consistente desde referencia o nombre del producto
    generateReferenceNumber(referencia, nombreProducto) {
      let refNormalizada = "";

      // Priorizar referencia, pero si está vacía o es "N/A", usar nombre del producto
      if (referencia && referencia.trim() !== "" && referencia.toUpperCase() !== "N/A") {
        refNormalizada = referencia.toUpperCase().replace(/[^A-Z0-9]/g, "");
      } else if (nombreProducto && nombreProducto.trim() !== "") {
        // Usar el nombre del producto, tomar las primeras palabras significativas
        refNormalizada = nombreProducto
          .toUpperCase()
          .replace(/[^A-Z0-9\s]/g, "") // Remover caracteres especiales
          .split(" ")
          .filter((word) => word.length > 2) // Solo palabras de más de 2 caracteres
          .slice(0, 3) // Tomar máximo 3 palabras
          .join("");
      }

      // Si aún está vacío, usar un valor determinístico basado en el nombre completo
      if (!refNormalizada && nombreProducto) {
        // Usar todo el nombre del producto como fallback, pero limpiándolo
        refNormalizada = nombreProducto
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "") // Solo letras y números
          .substring(0, 15); // Máximo 15 caracteres para el hash
      }

      // Si todavía está vacío, usar un valor por defecto fijo
      if (!refNormalizada) {
        refNormalizada = "PRODUCTO";
      }

      // Usar un hash simple pero consistente
      let hash = 0;
      for (let i = 0; i < refNormalizada.length; i++) {
        const char = refNormalizada.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convertir a 32bit entero
      }

      // Asegurar que sea positivo y 8 dígitos
      const numero = Math.abs(hash) % 99999999;
      return numero.toString().padStart(8, "0");
    },

    // Generar SKU para productos AI: 1{proveedor}{8-dígitos}
    generateAISKU(referencia, proveedorId, nombreProducto) {
      const refNumber = this.generateReferenceNumber(referencia, nombreProducto);
      const proveedorPadded = proveedorId.toString().padStart(4, "0");
      return "1" + proveedorPadded + refNumber;
    },

    // Generar PLU: categoria(2) + número secuencial(4)
    generateAIPLU: function (categoria, referencia, nombreProducto) {
      if (!categoria) return "";

      var refNumber = this.generateReferenceNumber(referencia, nombreProducto);

      // 🔥 CORREGIDO: Buscar el CÓDIGO de la categoría
      var categoriaCode = this.getCategoriaCode(categoria);
      if (!categoriaCode) return "";

      var categoriaPadded = categoriaCode.toString().padStart
        ? categoriaCode.toString().padStart(2, "0")
        : ("00" + categoriaCode).slice(-2);

      // Usar los últimos 4 dígitos del número de referencia
      var pluNumber = refNumber.slice(-4);
      return categoriaPadded + pluNumber;
    },

    updateProductCodes: function (product) {
      if (!product) return;

      // Generar códigos si hay proveedor (ya no requiere referencia obligatoria)
      if (this.selectedSupplier) {
        // Generar SKU basado en referencia o nombre del producto + proveedor
        product.sku_sugerido = this.generateAISKU(
          product.referencia,
          this.selectedSupplier.codigo,
          product.descripcion
        );

        // EAN = SKU si no se especifica otro
        if (!product.ean) {
          product.ean_sugerido = product.sku_sugerido;
        }

        // PLU basado en CÓDIGO de categoría + referencia o nombre
        if (product.categoria) {
          product.plu_sugerido = this.generateAIPLU(
            product.categoria,
            product.referencia,
            product.descripcion
          );
        }

        console.log("🔢 Códigos actualizados:", {
          referencia: product.referencia,
          descripcion: product.descripcion,
          categoria: product.categoria,
          categoriaCode: this.getCategoriaCode(product.categoria),
          sku: product.sku_sugerido,
          plu: product.plu_sugerido,
          ean: product.ean_sugerido,
        });
      }
    },

    // Actualizar estadísticas después del procesamiento
    updateStatsAfterProcessing: function (productCount, metadata) {
      this.stats.total_documents += 1;
      this.stats.total_products += productCount;
      this.stats.documents_this_month += 1;
      this.stats.products_this_month += productCount;

      // Actualizar tiempo promedio de procesamiento
      var newTime = (metadata.processing_time_ms || 0) / 1000;
      if (newTime > 0) {
        this.stats.avg_processing_time = (
          (this.stats.avg_processing_time * (this.stats.total_documents - 1) + newTime) /
          this.stats.total_documents
        ).toFixed(1);
      }
    },

    convertToBase64: function (file) {
      return new Promise(function (resolve, reject) {
        var reader = new FileReader();
        reader.onload = function () {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    // Quick action methods
    selectAllProducts: function () {
      this.selectedProducts = [...this.processingResults];
    },

    flagExpensiveProducts: function () {
      this.processingResults.forEach(function (product) {
        if (product.costo_unitario > 50000) {
          if (!product.alertas) product.alertas = [];
          if (product.alertas.indexOf("Producto de alto valor") === -1) {
            product.alertas.push("Producto de alto valor");
          }
        }
      });
      this.showMessage("Productos costosos marcados", "info");
    },

    reviewMissingData: function () {
      var missingCount = 0;
      this.processingResults.forEach(function (product) {
        var alerts = [];
        if (!product.descripcion || product.descripcion.trim() === "") {
          alerts.push("Descripción faltante");
        }
        if (!product.referencia || product.referencia === "N/A") {
          alerts.push("Referencia faltante");
        }
        if (product.costo_unitario <= 0) {
          alerts.push("Costo inválido");
        }

        if (alerts.length > 0) {
          product.alertas = alerts;
          missingCount++;
        }
      });

      this.showMessage(missingCount + " productos requieren atención", "warning");
    },

    autoFixPrices: function () {
      var fixedCount = 0;
      this.processingResults.forEach(function (product) {
        // Auto-corregir total si no coincide
        var expectedTotal = product.cantidad * product.costo_unitario;
        if (Math.abs(product.total - expectedTotal) > 0.01) {
          product.total = expectedTotal;
          fixedCount++;
        }
      });

      this.showMessage(fixedCount + " precios corregidos automáticamente", "success");
    },

    // Product editing - FORMULARIO COMPLETO
    editProduct: function (item) {
      this.editingProduct = Object.assign({}, item);
      this.editDialog = true;
    },

    updateEditTotals: function () {
      if (
        this.editingProduct &&
        this.editingProduct.cantidad &&
        this.editingProduct.costo_unitario
      ) {
        this.editingProduct.total =
          this.editingProduct.cantidad * this.editingProduct.costo_unitario;
        this.calculateMargin();
      }
    },

    calculateMargin: function () {
      if (
        this.editingProduct &&
        this.editingProduct.costo_unitario > 0 &&
        this.editingProduct.precio_venta_sugerido > 0
      ) {
        var margin =
          ((this.editingProduct.precio_venta_sugerido -
            this.editingProduct.costo_unitario) /
            this.editingProduct.costo_unitario) *
          100;
        this.editingProduct.margen_sugerido = Math.round(margin);
      }
    },

    saveProductEdit: function () {
      var self = this;

      if (!this.$refs.editForm.validate()) {
        this.showMessage("Por favor completa todos los campos requeridos", "warning");
        return;
      }

      this.savingEdit = true;

      // Simular guardado
      setTimeout(function () {
        if (self.editingProduct) {
          var index = self.processingResults.findIndex(
            (p) => p.id === self.editingProduct.id
          );
          if (index > -1) {
            // Actualizar totales antes de guardar
            self.editingProduct.total =
              self.editingProduct.cantidad * self.editingProduct.costo_unitario;

            // Reemplazar el producto en la lista
            self.processingResults.splice(index, 1, self.editingProduct);
          }
        }

        self.savingEdit = false;
        self.editDialog = false;
        self.editingProduct = null;
        self.showMessage("Producto actualizado exitosamente", "success");
      }, 1000);
    },

    duplicateProduct: function (item) {
      var newProduct = Object.assign({}, item);
      newProduct.id = Math.max(...this.processingResults.map((p) => p.id)) + 1;
      newProduct.descripcion += " (Copia)";
      this.processingResults.push(newProduct);
      this.showMessage("Producto duplicado", "info");
    },

    removeProduct: function (item) {
      var index = this.processingResults.findIndex(function (p) {
        return p.id === item.id;
      });
      if (index > -1) {
        this.processingResults.splice(index, 1);
        this.showMessage("Producto eliminado", "info");
      }
    },

    // Save products - SIMPLIFICADO PARA USAR ENDPOINTS EXISTENTES

    startNewProcess: function () {
      // Usar el método de reset completo
      this.resetFormCompleto();
    },

    /**
     * Buscar código de categoría desde el settings
     * @param {string} categoria - Valor de la categoría (ej: "COSMETICOS")
     * @returns {string} Código de la categoría (ej: "01")
     */
    getCategoriaCode: function (categoria) {
      if (!categoria || !this.availableCategories) return "";

      // Buscar en el array de categorías disponibles
      var categoriaObj = this.availableCategories.find(function (cat) {
        return cat.valor === categoria || cat.codigo === categoria;
      });

      return categoriaObj ? categoriaObj.codigo : "";
    },

    // Función para parsear productos AI al formato items
    parseProductsToItemsFormat() {
      const parsedItems = this.processingResults.map((aiProduct) => {
        // Buscar códigos de categoría y grupo

        const categoriaCode = this.getCategoryCode(aiProduct.categoria);
        const grupoCode = this.getGroupCode(aiProduct.grupo);

        return {
          // Campos obligatorios
          id_pos: window.store.store_id,
          sku: aiProduct.sku_sugerido || "",
          ean: aiProduct.ean_sugerido || "",
          plu: aiProduct.plu_sugerido || "",

          // Clasificación (CÓDIGOS, no texto)
          categoria: categoriaCode,
          grupo: grupoCode,
          marca: "001", // Marca genérica por defecto

          // Información del producto
          refe: aiProduct.referencia || "",
          nombre: aiProduct.descripcion,
          color: this.getColorCode(aiProduct.observaciones) || "99", // MULTICOLOR por defecto
          uom: aiProduct.unidad || "UND",
          talla: this.extractSize(aiProduct.observaciones) || "U",

          // Precios y costos
          cantidad: parseFloat(aiProduct.cantidad) || 0,
          costo: parseFloat(aiProduct.costo_unitario) || 0,
          precio: parseFloat(aiProduct.precio_venta_sugerido) || 0,
          precioB: 0.0,
          precioC: 0.0,
          precioD: 0.0,

          // Información del proveedor
          id_proveedor: this.selectedSupplier.codigo || "",
          proveedor: this.selectedSupplier.nombre || "",
          cod_product_proveedor: aiProduct.referencia || "",

          // Fechas
          first_in: new Date().toISOString().split("T")[0],
          last_in: new Date().toISOString().split("T")[0],

          // Campos específicos de calzado (opcional)
          tacon: null,
          suela: null,
          msuela: null,
          mzapato: null,

          // Campos de control
          audit: 0,
          descuento: 0.0,
          status: 1, // Activo
          concepto: "INGRESO AI",
          modelo: null,
          tallas: aiProduct.observaciones || "",
          tax: 19.0,
          ean_brand: null,
          color_code: this.getColorCode(aiProduct.observaciones) || "99",
        };
      });

      return parsedItems;
    },

    // Métodos auxiliares para mapeo de códigos
    getCategoryCode(categoryName) {
      const settings = window.settings || {};
      const categoria = settings.CATEGORIA?.find((cat) => cat.valor === categoryName);
      return categoria?.codigo || "99"; // OTROS por defecto
    },

    getGroupCode(groupName) {
      const settings = window.settings || {};
      const grupo = settings.GRUPO?.find((grp) => grp.valor === groupName);
      return grupo?.codigo || "01"; // PRODUCTOS BÁSICOS por defecto
    },

    getColorCode(observaciones) {
      if (!observaciones) return "99";

      const colorMap = {
        BLANCO: "01",
        NEGRO: "02",
        GRIS: "03",
        AZUL: "04",
        ROJO: "05",
        VERDE: "06",
        AMARILLO: "07",
        NARANJA: "08",
        ROSA: "09",
        VIOLETA: "10",
        MARRÓN: "11",
        BEIGE: "12",
      };

      const obs = observaciones.toUpperCase();
      for (const [color, code] of Object.entries(colorMap)) {
        if (obs.includes(color)) return code;
      }

      return "99"; // MULTICOLOR por defecto
    },

    extractSize(observaciones) {
      if (!observaciones) return "U";

      const sizeRegex = /\b(XS|S|M|L|XL|XXL|\d+)\b/i;
      const match = observaciones.match(sizeRegex);
      return match ? match[0].toUpperCase() : "U";
    },

    // Función final para crear la orden - CORREGIDA
    createPurchaseOrder: function (data) {
      var self = this;
      var parsedItems = data;

      // 🆕 CREAR ARRAY MASTER - Solo cambiar id_pos a primeros 6 caracteres
      self.masterItems = parsedItems.map(function (item) {
        var masterItem = Object.assign({}, item); // Copia completa del item
        masterItem.id_pos = item.id_pos.substring(0, 6); // Solo cambiar id_pos
        return masterItem;
      });

      var qry = {
        doc: {
          doc_type: "OP",
          doc_value: this.totalCost,
          store: window.store.store_id,
          userId: window.userId || window.profile.user_email,
        },
        items: parsedItems,
        master: self.masterItems,
        paidTo: this.selectedSupplier,
        seller: {
          store_id: window.store.store_id,
          PartyName: window.store.store_name || "Tienda Principal",
        },
        store: window.profile.store_id,
        payments: [
          {
            payment_code: this.motive.codigo,
            payment_concept: this.motive.valor,
            payment_reference: "",
            payment_value: this.totalCost,
          },
        ],
      };

      console.log("🚀 Enviando orden de compra:", qry);

      // ✅ ACTIVAR la llamada al servidor
      webserver(
        "put_stock",
        qry,
        function (response) {
          self.savingProducts = false;

          if (response && response.success !== false) {
            self.showMessage("✅ Orden de compra creada exitosamente", "success", 5000);
            self.orderDocument = response;

            self.showOrderDocument = true;

            // 🔄 RESET COMPLETO del formulario
            self.resetFormCompleto();

            // 📊 Actualizar estadísticas
            self.updateStatsAfterOrder();
          } else {
            self.showMessage(
              "❌ Error al crear orden: " + (response?.error || "Error desconocido"),
              "error"
            );
          }
        },
        function (error) {
          self.savingProducts = false;
          self.showMessage("❌ Error de conexión: " + error, "error");
        }
      );
    },

    // 📊 Actualizar estadísticas después de crear orden
    updateStatsAfterOrder: function () {
      console.log("📊 Actualizando estadísticas después de crear orden...");

      // Incrementar contadores locales inmediatamente
      this.stats.total_documents += 1;
      this.stats.documents_this_month += 1;

      // Opcional: Recargar estadísticas reales del servidor
      setTimeout(() => {
        this.loadRealStats();
      }, 1000); // Esperar 1 segundo para que se procese en el servidor
    },

    // 🔄 RESET COMPLETO del formulario después de crear orden
    resetFormCompleto: function () {
      console.log("🔄 Reseteando formulario completo...");

      // 1️⃣ Limpiar archivos y previews
      this.selectedFile = null;
      this.imagePreview = null;
      this.dragOver = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }

      // 2️⃣ Limpiar supplier y documentos
      this.selectedSupplier = null;
      this.documentType = "invoice";
      this.motive = null;

      // 3️⃣ Limpiar estados de procesamiento
      this.processing = false;
      this.processingStep = 1;
      this.processingMessage = "Preparando imagen...";
      this.processingDetail = "";
      this.savingProducts = false;

      // 4️⃣ Limpiar resultados y datos
      this.processingResults = [];
      this.documentInfo = {};
      this.selectedProducts = [];

      // 5️⃣ Limpiar dialogs de edición
      this.editDialog = false;
      this.editingProduct = null;
      this.editFormValid = false;
      this.savingEdit = false;

      // 6️⃣ Resetear formularios si existen
      if (this.$refs.editForm) {
        this.$refs.editForm.reset();
        this.$refs.editForm.resetValidation();
      }

      // 7️⃣ Forzar reactividad
      var self = this;
      this.$nextTick(function () {
        self.showMessage("📝 Formulario listo para nuevo documento", "info", 3000);
      });
    },
    executeFullProcess() {
      if (!this.canSaveProducts) {
        this.showMessage(
          "Completa todos los productos antes de crear ingreso",
          "warning"
        );
        return;
      }

      this.savingProducts = true;

      try {
        // 1. Parsear productos al formato items
        const parsedItems = this.parseProductsToItemsFormat();

        // 2. Crear la orden/entrada
        this.createPurchaseOrder(parsedItems);
      } catch (error) {
        this.savingProducts = false;
        this.showMessage("Error en el procesamiento: " + error.message, "error");
      }
    },
  },

  // En el formulario de edición, agregar watchers
  watch: {
    "editingProduct.referencia": function () {
      if (this.editingProduct) {
        this.updateProductCodes(this.editingProduct);
      }
    },

    "editingProduct.descripcion": function () {
      if (this.editingProduct) {
        this.updateProductCodes(this.editingProduct);
      }
    },

    "editingProduct.categoria": function () {
      if (this.editingProduct) {
        this.updateProductCodes(this.editingProduct);
      }
    },
  },
};
</script>

<style scoped>
/* Estilos existentes + nuevos para formulario */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
}

.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.upload-area {
  border: 3px dashed #bdc3c7;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover:not(.disabled),
.upload-area.drag-over:not(.disabled) {
  border-color: #3498db;
  background: #e3f2fd;
  transform: scale(1.02);
}

.upload-area.has-file {
  cursor: default;
  padding: 20px;
}

.upload-area.disabled {
  cursor: not-allowed;
  background: #f0f0f0;
  border-color: #ccc;
}

.upload-content {
  color: #666;
}

.file-preview {
  display: flex;
  gap: 20px;
  align-items: center;
  width: 100%;
  position: relative;
}

.preview-image-container {
  flex: 0 0 200px;
  position: relative;
}

.preview-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.file-info {
  flex: 1;
  text-align: left;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

/* Nuevos estilos para celdas read-only */
.product-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-text {
  font-size: 0.875rem;
  color: #333;
}

.document-info p {
  margin-bottom: 8px;
  color: #666;
}

.financial-summary {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
}

.summary-item {
  text-align: center;
}

.summary-label {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.results-info {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.gap-2 > * {
  margin-right: 8px;
  margin-bottom: 8px;
}

@media (max-width: 960px) {
  .file-preview {
    flex-direction: column;
    text-align: center;
  }

  .preview-image-container {
    flex: none;
  }

  .minimal-toolbar {
    flex-wrap: wrap;
    gap: 16px;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>
